import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { MarketplaceExtendedQuery, MarketplaceItemExtended, MarketplaceItemStatus, ProcessProviderId } from '@fgb/core';
import { SortBarOption, SortOrder } from '@fgb/portal-component-library/src/lib/shared/sort-bar';
import { MarketplaceListSortOption } from './marketplace-list-sort-bar.enum';
import moment from 'moment/moment';

@Component({
  selector: 'fgbcl-marketplace-list',
  templateUrl: './marketplace-list.component.html',
  styleUrls: ['./marketplace-list.component.scss'],
})
export class MarketplaceListComponent implements OnInit, OnDestroy {
  @Input() take: number;
  @Input() isList: boolean;
  @Input() showSortBar: boolean = true;
  @Input() marketplaceItemTypes: ProcessProviderId[];
  @Input() marketplaceItemView: boolean;
  @Input() eventItemView: boolean;
  @Input() productsItemView: boolean;
  @Input() defaultSort: MarketplaceListSortOption = MarketplaceListSortOption.Points;
  @Input() showStatusFilter: boolean = false;
  @Input() featuredIcon: string = 'star';
  sortBarOptions: SortBarOption[];
  sortOrderType = SortOrder;

  marketplaceItems$: Observable<MarketplaceItemExtended[] | undefined>;
  originalMarketplaceItem: MarketplaceItemExtended[];
  marketplaceItemsSubscription: Subscription;
  filteredMarketplaceItems: MarketplaceItemExtended[];
  filterTypesList = [{ label: 'reward.filter.status.all', value: MarketplaceItemStatus.All }];
  selectedState: number[] = [MarketplaceItemStatus.All];
  MarketplaceItemStatus = MarketplaceItemStatus;
  constructor(private marketplaceExtendedQuery: MarketplaceExtendedQuery) {}

  ngOnInit() {
    this.queryMarketplaceItems();
    this.subscribeAndSortMarketplaceItems();
    this.defineSortBarOptions();
  }

  ngOnDestroy() {
    if (this.marketplaceItemsSubscription) {
      this.marketplaceItemsSubscription.unsubscribe();
    }
  }

  queryMarketplaceItems() {
    if (this.marketplaceItemTypes) {
      this.marketplaceItems$ = this.marketplaceExtendedQuery.selectExtendedMarketplaceItemsOfType(...this.marketplaceItemTypes);
    } else {
      this.marketplaceItems$ = this.marketplaceExtendedQuery.selectExtendedMarketplaceItems();
    }
  }

  subscribeAndSortMarketplaceItems() {
    this.marketplaceItemsSubscription = this.marketplaceItems$.subscribe((items) => {
      if (items) {
        items.filter((item) => {
          const today = moment().utc();
          const endDate = moment.utc(item.ItemEndDate);
          return !item.ComingSoon || (item.ComingSoon && endDate.isAfter(today));
        });
        this.originalMarketplaceItem = [...items];
        items = this._sortItems(items);
        this.filteredMarketplaceItems = items;
      }
    });
  }

  defineSortBarOptions() {
    this.sortBarOptions = [
      {
        propertyToSort: 'PointValue',
        shouldToggle: true,
        order: this.sortOrderType.Descending,
        labelASC: 'fgbcl.points.low.to.high',
        labelDESC: 'fgbcl.points.high.to.low',
      },
      {
        propertyToSort: 'ItemEndDate',
        order: this.sortOrderType.Ascending,
        shouldToggle: false,
        labelASC: 'fgbcl.sortby.ending.soon',
      },
      {
        propertyToSort: 'AvailableCount',
        order: this.sortOrderType.Descending,
        shouldToggle: true,
        labelASC: 'fgbcl.limited.availability',
        labelDESC: 'fgbcl.high.availability',
      },
      {
        propertyToSort: 'DisplayName',
        order: this.sortOrderType.Descending,
        shouldToggle: true,
        labelASC: 'fgbcl.sortby.name.atoz',
        labelDESC: 'fgbcl.sortby.name.ztoa',
      },
    ];
  }

  resetOrder() {
    this.subscribeAndSortMarketplaceItems();
  }

  private _sortItems(items: MarketplaceItemExtended[]): MarketplaceItemExtended[] {
    let sortedItemsMap = new Map<string, MarketplaceItemExtended[]>();
    // split out for all marketplace status
    for (let status in MarketplaceItemStatus) {
      if (isNaN(Number(status))) {
        sortedItemsMap.set(
          status,
          items
            .filter((x) => x.Status === (MarketplaceItemStatus as any)[status])
            .sort((a: MarketplaceItemExtended, b: MarketplaceItemExtended) => this.sortByType(a, b))
        );
      }
    }

    let sortedItems: MarketplaceItemExtended[] = [];

    const orderedStatuses = [
      MarketplaceItemStatus.Available,
      MarketplaceItemStatus.MemberLimitReached,
      MarketplaceItemStatus.ComingSoon,
      MarketplaceItemStatus.SoldOut,
      MarketplaceItemStatus.NotEnoughPoints,
    ];

    const isAllSelected = this.selectedState[0] === MarketplaceItemStatus.All;

    orderedStatuses.forEach((status) => {
      const shouldInclude = isAllSelected || this.filterTypesList.some((type) => type.value === status);
      if (shouldInclude) {
        const items = sortedItemsMap.get(MarketplaceItemStatus[status]);
        if (items) {
          sortedItems.push(...items);
        }
        if (status === MarketplaceItemStatus.Available) {
          sortedItems = this.sortItemsFavouriteAndNewest(sortedItems);
        }
      }
    });

    return sortedItems;
  }

  sortItemsFavouriteAndNewest(marketplaceItemExtended: MarketplaceItemExtended[]) {
    let favouriteAndNewestItems: MarketplaceItemExtended[] = [];
    let favouriteAndLimitedItems: MarketplaceItemExtended[] = [];

    // Sorted favouriate items
    const itemsLimited = marketplaceItemExtended.filter((item) => item.AvailabilityCount > 0 && item.AvailabilityCount <= 10);
    if (itemsLimited.length > 0) {
      favouriteAndLimitedItems.push(...itemsLimited);
      marketplaceItemExtended = marketplaceItemExtended.filter(
        (item) => favouriteAndLimitedItems.findIndex((itemIndex) => itemIndex.Id === item.Id) === -1
      );
    }

    // Sorted favouriate items
    const itemsFavourite = marketplaceItemExtended.filter((item) => item.Favourite == true && !this.isNewItem(item));
    if (itemsFavourite.length > 0) {
      favouriteAndNewestItems.push(...itemsFavourite);
      marketplaceItemExtended = marketplaceItemExtended.filter(
        (item) => favouriteAndNewestItems.findIndex((itemIndex) => itemIndex.Id === item.Id) === -1
      );
    }

    // Sorted new items
    const favouriateNew = marketplaceItemExtended.filter((item) => item.Active && item.Favourite && this.isNewItem(item));
    if (favouriateNew.length > 0) {
      favouriteAndNewestItems.push(...favouriateNew);
      marketplaceItemExtended = marketplaceItemExtended.filter(
        (item) => favouriteAndNewestItems.findIndex((itemIndex) => itemIndex.Id === item.Id) === -1
      );
    }
    const itemsNew = marketplaceItemExtended.filter((item) => item.Active && this.isNewItem(item));
    if (itemsNew.length > 0) {
      favouriteAndNewestItems.push(...itemsNew);
      marketplaceItemExtended = marketplaceItemExtended.filter(
        (item) => favouriteAndNewestItems.findIndex((itemIndex) => itemIndex.Id === item.Id) === -1
      );
    }

    if (favouriteAndLimitedItems.length > 0) {
      favouriteAndLimitedItems.sort((a, b) => b.AvailableCount - a.AvailableCount);
      console.log(favouriteAndLimitedItems);

      favouriteAndNewestItems.push(...favouriteAndLimitedItems);
    }

    if (marketplaceItemExtended.length > 0) {
      favouriteAndNewestItems.push(...marketplaceItemExtended);
    }

    if (this.defaultSort !== MarketplaceListSortOption.Status) {
      favouriteAndNewestItems.sort((a: MarketplaceItemExtended, b: MarketplaceItemExtended) => this.sortByType(a, b));
    }
    return favouriteAndNewestItems;
  }

  /**
   * Method to check the given item is new or not.
   * @param item marketplace item to check new.
   * @returns return the result in boolean, if it is new item or not.
   */
  private isNewItem(marketplaceItemExtended: MarketplaceItemExtended): boolean {
    const threeDays = 3.0;
    const startDate = moment().diff(marketplaceItemExtended?.ItemStartDate, 'days');

    return startDate <= threeDays;
  }

  sortByType(a: MarketplaceItemExtended, b: MarketplaceItemExtended) {
    switch (this.defaultSort) {
      case MarketplaceListSortOption.Points:
        return this.sortByPoints(a, b);
      case MarketplaceListSortOption.Ordinal:
        return this.sortByOrdinal(a, b);
      default:
        return 0;
    }
  }

  private sortByPoints(a: MarketplaceItemExtended, b: MarketplaceItemExtended) {
    return a.PointValue - b.PointValue;
  }

  private sortByOrdinal(a: MarketplaceItemExtended, b: MarketplaceItemExtended) {
    return a.Ordinal - b.Ordinal;
  }

  handleFilterTypesListEmitter(filterTypes: any[]) {
    this.filterTypesList = [...filterTypes];
  }

  handleSelectedStateEmitter(selectedState: number[]) {
    this.selectedState = [...selectedState];
    this.filteredMarketplaceItems = this._sortItems([...this.originalMarketplaceItem]);
  }

  isStateInFilter(status: MarketplaceItemStatus) {
    return this.selectedState.includes(status);
  }
}
